import { concat, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { DataStatus, EpicDeps } from 'src/core/engine/types';
import { AppendUserState, UserSession } from 'src/core/state/user';
import { Actions, AppendCartState, Msg, TypeMap } from 'src/core/state/cart';
import { fetchCart } from 'src/features/cart/epics/fetchCart';
import { fetchMineCartId } from 'src/core/state/cart/utils/fetchMineCartId';
import { BasketMsg } from 'src/components/Basket/feature/basket.actions';

type State = AppendCartState & AppendUserState;

const MISSING_CART_MESSAGES = [
    'could not find a cart',
    "the cart isn't active",
    'active cart does not exist',
    'no such entity with cart_id',
];

function isMissingCartError(err: any): boolean {
    const message = String(err?.message || err || '').toLowerCase();
    return MISSING_CART_MESSAGES.some((known) => message.includes(known));
}

/**
 * This epic handles the fetching of all carts
 * @param action$
 * @param state$
 * @param deps
 */
export function cartFetch2(action$: Observable<any>, state$: Observable<State>, deps: EpicDeps): Observable<any> {
    return action$.pipe(
        ofType<Actions, TypeMap['Cart.Fetch']>('Cart.Fetch'),
        withLatestFrom(
            state$.pipe(map((s) => s?.cart?.cartId)),
            state$.pipe(map((s) => s?.user?.session)),
            state$.pipe(map((s) => s?.cart?.items)),
        ),
        switchMap(([{ payload }, cartId, userSession, items]) => {
            /**
             *
             * Possible states:
             *
             * 1. user, where a previous cart was retrieved and it's ID was sent in this actions payload
             * 2. user with a previous cart
             * 3. user with no previous cart
             * 4. guest with no previous cart
             * 5. guest with previous cart
             *
             */

            const ajax$ = (() => {
                if (userSession === UserSession.SignedIn) {
                    // if an ID was given in the action, use it first
                    if (payload && payload.cartId) {
                        return fetchCart(payload.cartId, deps);
                    }
                    // if the cartId is absent, lookup for the user first
                    if (!cartId) {
                        return fetchMineCartId(deps).pipe(mergeMap((id) => fetchCart(id, deps)));
                    }
                }
                // default is to use the ID from the store
                return fetchCart(cartId, deps);
            })();

            return ajax$.pipe(
                mergeMap((resp) => {
                    const hadItems = Array.isArray(items) && items.length > 0;
                    const fetchedEmpty = !resp?.items || resp.items.length === 0;
                    const switchedCart = Boolean(resp?.cartId && cartId && String(resp.cartId) !== String(cartId));
                    // After sign-in Magento may resolve customerCart (empty) instead of the guest cart.
                    if (hadItems && fetchedEmpty && switchedCart) {
                        return of(Msg('Cart.SetStatus', DataStatus.Success));
                    }
                    return of(Msg('Cart.FetchSuccess', resp), BasketMsg('Basket.Refresh'), Msg('Cart.Persist'));
                }),
                catchError((err) => {
                    // A failed fetch after sign-in must not wipe guest items via Cart.Reset,
                    // but a cart the backend no longer knows about has to be dropped -
                    // otherwise the stale items stay in the store and localStorage forever.
                    if (Array.isArray(items) && items.length > 0 && !isMissingCartError(err)) {
                        return of(Msg('Cart.FetchError', err));
                    }
                    return concat(of(Msg('Cart.FetchError', err), Msg('Cart.Reset')));
                }),
            );
        }),
    );
}
